import { useState, useEffect, useCallback, useRef } from "react";
import { Capacitor } from "@capacitor/core";
import { Geolocation } from "@capacitor/geolocation";
import { PushNotifications } from "@capacitor/push-notifications";
import { Camera } from "@capacitor/camera";

export type PermissionType = "location" | "notifications" | "camera";

interface PermissionState {
  location: boolean;
  notifications: boolean;
  camera: boolean;
}

interface PermissionStatus {
  granted: boolean;
  denied: boolean;
  prompt: boolean;
}

const STORAGE_KEY = "berastays_permissions_v2";
const ONBOARDING_COMPLETE_KEY = "berastays_onboarding_complete_v2";
const FIRST_INSTALL_KEY = "berastays_first_install_detected";

/**
 * Check if this is truly the first time user is using the app
 * Uses multiple indicators to detect if cache was cleared
 */
export const isFirstTimeUser = (): boolean => {
  // Check if user has ever logged in (stored in localStorage)
  const hasLoggedInBefore = localStorage.getItem("berastays_has_logged_in");
  
  // Check if permissions were ever set
  const permissionsVersion = localStorage.getItem(STORAGE_KEY);
  const onboardingComplete = localStorage.getItem(ONBOARDING_COMPLETE_KEY);
  
  // If any of these exist, user has used app before
  if (hasLoggedInBefore || permissionsVersion || onboardingComplete) {
    return false;
  }
  
  return true;
};

/**
 * Mark that user has logged in (survives cache clearing less likely)
 */
export const markUserAsReturning = () => {
  localStorage.setItem("berastays_has_logged_in", "true");
  localStorage.setItem(FIRST_INSTALL_KEY, "true");
};

/**
 * Check if onboarding should be shown
 */
export const shouldShowOnboarding = (): boolean => {
  // Always show if first time
  if (isFirstTimeUser()) {
    return true;
  }
  
  // Check if onboarding was completed in this version
  const onboardingComplete = localStorage.getItem(ONBOARDING_COMPLETE_KEY);
  if (onboardingComplete === "true") {
    return false;
  }
  
  // Check legacy key for backward compatibility
  const legacyComplete = localStorage.getItem("berastays_onboarding_complete");
  if (legacyComplete === "true") {
    // Migrate to new key
    localStorage.setItem(ONBOARDING_COMPLETE_KEY, "true");
    return false;
  }
  
  return true;
};

/**
 * Mark onboarding as complete
 */
export const completeOnboarding = (permissions: PermissionState) => {
  localStorage.setItem(ONBOARDING_COMPLETE_KEY, "true");
  localStorage.setItem(STORAGE_KEY, JSON.stringify(permissions));
  localStorage.setItem(FIRST_INSTALL_KEY, "true");
};

/**
 * Get stored permissions
 */
export const getStoredPermissions = (): PermissionState | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
    
    // Try legacy key
    const legacy = localStorage.getItem("berastays_permissions");
    if (legacy) {
      const parsed = JSON.parse(legacy);
      // Migrate to new key
      localStorage.setItem(STORAGE_KEY, legacy);
      return parsed;
    }
  } catch {
    // Ignore parse errors
  }
  return null;
};

/**
 * Check if running in native Capacitor app
 */
const isNativePlatform = (): boolean => {
  return Capacitor.isNativePlatform();
};

/**
 * Hook to manage all app permissions
 * Works for both native (Capacitor) and web platforms
 */
export const usePermissions = () => {
  const [permissions, setPermissions] = useState<PermissionState>({
    location: false,
    notifications: false,
    camera: false,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const initialized = useRef(false);

  // Check all permission statuses
  const checkPermissions = useCallback(async (): Promise<PermissionState> => {
    const result: PermissionState = {
      location: false,
      notifications: false,
      camera: false,
    };

    if (isNativePlatform()) {
      try {
        // Check location
        const locStatus = await Geolocation.checkPermissions();
        result.location = locStatus.location === "granted" || locStatus.coarseLocation === "granted";
      } catch (e) {
        console.log("[Permissions] Location check error:", e);
      }

      try {
        // Check push notifications
        const pushStatus = await PushNotifications.checkPermissions();
        result.notifications = pushStatus.receive === "granted";
      } catch (e) {
        console.log("[Permissions] Push check error:", e);
      }

      try {
        // Check camera
        const camStatus = await Camera.checkPermissions();
        result.camera = camStatus.camera === "granted";
      } catch (e) {
        console.log("[Permissions] Camera check error:", e);
      }
    } else {
      // Web platform checks
      try {
        // Check location permission via navigator.permissions API
        if ("permissions" in navigator) {
          const locPerm = await navigator.permissions.query({ name: "geolocation" as PermissionName });
          result.location = locPerm.state === "granted";
        }
      } catch (e) {
        console.log("[Permissions] Web location check error:", e);
      }

      try {
        // Check notification permission
        if ("Notification" in window) {
          result.notifications = Notification.permission === "granted";
        }
      } catch (e) {
        console.log("[Permissions] Web notification check error:", e);
      }

      try {
        // Check camera permission
        if ("permissions" in navigator) {
          const camPerm = await navigator.permissions.query({ name: "camera" as PermissionName });
          result.camera = camPerm.state === "granted";
        }
      } catch (e) {
        console.log("[Permissions] Web camera check error:", e);
      }
    }

    return result;
  }, []);

  // Request specific permission
  const requestPermission = useCallback(async (type: PermissionType): Promise<boolean> => {
    if (isNativePlatform()) {
      try {
        switch (type) {
          case "location": {
            const locResult = await Geolocation.requestPermissions();
            const granted = locResult.location === "granted" || locResult.coarseLocation === "granted";
            setPermissions((prev) => ({ ...prev, location: granted }));
            return granted;
          }
          case "notifications": {
            const pushResult = await PushNotifications.requestPermissions();
            const granted = pushResult.receive === "granted";
            setPermissions((prev) => ({ ...prev, notifications: granted }));
            return granted;
          }
          case "camera": {
            const camResult = await Camera.requestPermissions();
            const granted = camResult.camera === "granted";
            setPermissions((prev) => ({ ...prev, camera: granted }));
            return granted;
          }
        }
      } catch (e) {
        console.error(`[Permissions] Error requesting ${type}:`, e);
        return false;
      }
    } else {
      // Web platform requests
      try {
        switch (type) {
          case "location": {
            // Web location is requested automatically when calling getCurrentPosition
            const granted = await new Promise<boolean>((resolve) => {
              navigator.geolocation.getCurrentPosition(
                () => resolve(true),
                () => resolve(false),
                { timeout: 5000 }
              );
            });
            setPermissions((prev) => ({ ...prev, location: granted }));
            return granted;
          }
          case "notifications": {
            if (!("Notification" in window)) return false;
            const result = await Notification.requestPermission();
            const granted = result === "granted";
            setPermissions((prev) => ({ ...prev, notifications: granted }));
            return granted;
          }
          case "camera": {
            const granted = await navigator.mediaDevices
              .getUserMedia({ video: true })
              .then(() => true)
              .catch(() => false);
            setPermissions((prev) => ({ ...prev, camera: granted }));
            return granted;
          }
        }
      } catch (e) {
        console.error(`[Permissions] Web error requesting ${type}:`, e);
        return false;
      }
    }
    return false;
  }, []);

  // Initialize - check if we need to show onboarding
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    const init = async () => {
      setIsLoading(true);
      
      // Check if we should show onboarding
      const needsOnboarding = shouldShowOnboarding();
      setShowOnboarding(needsOnboarding);
      
      if (!needsOnboarding) {
        // Check current permissions
        const currentPerms = await checkPermissions();
        setPermissions(currentPerms);
        
        // Update stored permissions
        const stored = getStoredPermissions();
        if (stored) {
          // Merge stored with current (current is more accurate)
          setPermissions((prev) => ({ ...prev, ...currentPerms }));
        }
      }
      
      setIsLoading(false);
    };

    init();
  }, [checkPermissions]);

  return {
    permissions,
    isLoading,
    showOnboarding,
    checkPermissions,
    requestPermission,
    isFirstTime: isFirstTimeUser(),
  };
};

/**
 * Hook specifically for location operations
 * Uses cached permission from onboarding
 * Only requests "in time" if GPS is disabled (not for permission)
 */
export const useLocationWithPermission = () => {
  const [isLocating, setIsLocating] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [lastLocation, setLastLocation] = useState<{ lat: number; lng: number } | null>(null);

  // Get cached location from onboarding or session
  const getCachedLocation = useCallback((): { lat: number; lng: number } | null => {
    try {
      // Check session storage first (faster)
      const session = sessionStorage.getItem("berastays_location_cache");
      if (session) {
        const data = JSON.parse(session);
        if (Date.now() - data.timestamp < 30 * 60 * 1000) { // 30 min cache
          return { lat: data.lat, lng: data.lng };
        }
      }
      
      // Check localStorage
      const stored = localStorage.getItem("berastays_location_cache");
      if (stored) {
        const data = JSON.parse(stored);
        if (Date.now() - data.timestamp < 60 * 60 * 1000) { // 1 hour cache
          return { lat: data.lat, lng: data.lng };
        }
      }
    } catch {
      // Ignore errors
    }
    return null;
  }, []);

  // Cache location
  const cacheLocation = useCallback((lat: number, lng: number) => {
    const data = { lat, lng, timestamp: Date.now() };
    const json = JSON.stringify(data);
    sessionStorage.setItem("berastays_location_cache", json);
    localStorage.setItem("berastays_location_cache", json);
    setLastLocation({ lat, lng });
  }, []);

  // Get current location (uses cached permission, only fails if GPS disabled)
  const getCurrentLocation = useCallback(async (): Promise<{ lat: number; lng: number } | null> => {
    setIsLocating(true);
    setLocationError(null);

    try {
      // Check if we have cached location first
      const cached = getCachedLocation();
      if (cached) {
        setLastLocation(cached);
        setIsLocating(false);
        return cached;
      }

      if (isNativePlatform()) {
        // Native: Use Capacitor Geolocation
        const position = await Geolocation.getCurrentPosition({
          enableHighAccuracy: true,
          timeout: 20000,
        });
        
        const location = { 
          lat: position.coords.latitude, 
          lng: position.coords.longitude 
        };
        cacheLocation(location.lat, location.lng);
        setIsLocating(false);
        return location;
      } else {
        // Web: Use browser geolocation
        const position = await new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 20000,
            maximumAge: 60000,
          });
        });
        
        const location = { 
          lat: position.coords.latitude, 
          lng: position.coords.longitude 
        };
        cacheLocation(location.lat, location.lng);
        setIsLocating(false);
        return location;
      }
    } catch (error: any) {
      console.error("[useLocationWithPermission] Error:", error);
      
      // Determine specific error
      if (error.code === 1 || error.message?.includes("permission")) {
        setLocationError("Permissão de localização negada. Por favor, ative nas configurações do dispositivo.");
      } else if (error.code === 2 || error.message?.includes("position unavailable")) {
        setLocationError("GPS desativado ou indisponível. Por favor, ative o GPS nas configurações.");
      } else if (error.code === 3 || error.message?.includes("timeout")) {
        setLocationError("Tempo esgotado ao obter localização. Verifique se o GPS está ativo.");
      } else {
        setLocationError("Não foi possível obter a localização. Verifique se o GPS está ativado.");
      }
      
      setIsLocating(false);
      return null;
    }
  }, [getCachedLocation, cacheLocation]);

  // Check if GPS is enabled (only for native, web can't detect this reliably)
  const checkGPSEnabled = useCallback(async (): Promise<boolean> => {
    if (!isNativePlatform()) {
      // Web: try to get location to see if it works
      try {
        await new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
        });
        return true;
      } catch {
        return false;
      }
    }
    
    // Native: Check if we can get position
    try {
      await Geolocation.getCurrentPosition({ timeout: 5000 });
      return true;
    } catch {
      return false;
    }
  }, []);

  return {
    getCurrentLocation,
    checkGPSEnabled,
    isLocating,
    locationError,
    lastLocation,
    getCachedLocation,
  };
};

export default usePermissions;
